import Text from 'components/Text/Text';
import React, { useContext, useState } from 'react';
import { postMediaFile } from 'backend/additionalInformation';
import { IEditGameContext, EditGameContext } from '../../../../../../editGameState/editGameContext';
import { RequestEntity } from 'types';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import breadcrumbs from 'images/breadcrumbs.svg';
import classNames from 'classnames';
import style from './FormDataImgUploader.module.scss';

const FormDataImgUploader: React.FC = () => {
	const { changeTypeOfModalWindow, setFile, deletePhoto, gameID, iconFileId, iconFilePath, sourceUrlLocally } =
		useContext<IEditGameContext>(EditGameContext);
	const [isShowLoader, setIsShowLoader] = useState<boolean>(false);
	const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
		iconFileId || iconFilePath,
		'uploadPhotoNecessary',
		'addingImg',
	);

	const imgBlankClasses = classNames(style.imgBlank, { [style.error]: isShowValidationError });
	const uploadBtnClasses = classNames(style.btn, style.addOrEditBtn, style.posAbsolute, {
		[style.visible]: !sourceUrlLocally && !iconFilePath,
	});
	const replaceBtnClasses = classNames(style.btn, style.addOrEditBtn, {
		[style.visible]: sourceUrlLocally || iconFilePath,
	});
	const deleteBtnClasses = classNames(style.btn, style.deleteBtn, {
		[style.visible]: sourceUrlLocally || iconFilePath,
	});

	const sendForm = async (image: File) => {
		setIsShowLoader(true);
		const formData = new FormData();
		formData.append('file', image, image.name);
		const sourceUrl = URL.createObjectURL(image);
		try {
			const response = await postMediaFile({ data: formData });
			setFile(response.data.fileId, sourceUrl);
			setIsShowLoader(false);
		} catch (err) {
			setIsShowLoader(false);
			changeTypeOfModalWindow('fileUploadError');
		}
	};

	const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		const maxFileSizeMB = 1;
		const imgFile = e.target.files ? e.target.files[0] : '';
		if (!imgFile) {
			return;
		} else if (imgFile.size > 1000 * 1000 * maxFileSizeMB) {
			changeTypeOfModalWindow('imgSizeExceeded');
		} else if (imgFile.type === 'image/jpeg' || imgFile.type === 'image/png') {
			sendForm(imgFile);
		} else {
			changeTypeOfModalWindow('invalidFileFormat');
		}
		e.target.value = '';
	};

	return (
		<div className={style.container} data-test="FormDataImgUploader">
			<div className={style.imgWrapper}>
				{iconFilePath && !sourceUrlLocally ? (
					<ImgUploaderByEntityId
						resource={RequestEntity.Games}
						entityID={gameID}
						photoSize="Medium"
						loaderSize="big"
					/>
				) : null}
				{sourceUrlLocally ? (
					<div className={style.fileImgWrapper}>
						<img className={style.img} src={sourceUrlLocally} alt="image" />
					</div>
				) : null}
				{!sourceUrlLocally && !iconFilePath ? (
					<>
						<div className={imgBlankClasses}>
							<svg>
								<use xlinkHref={`${breadcrumbs}#gameDefaultAvatar`} />
							</svg>
						</div>
						{isShowValidationError && <div className={style.imgErrorText}>{validationErrorMessage}</div>}
					</>
				) : null}
				{isShowLoader ? (
					<div className={style.loaderWrapper}>
						<LoaderWithProps loaderSize="small" />
					</div>
				) : null}
			</div>
			<input
				id="addingImg"
				className={style.hidden}
				type="file"
				accept="image/jpeg,image/png"
				onChange={handleChangeFile}
			/>
			<div className={style.btnWrapper}>
				<label className={style.label} htmlFor="addingImg">
					<div className={uploadBtnClasses}>
						<svg>
							<use xlinkHref={`${breadcrumbs}#uploadPhoto`} />
						</svg>
						<Text translationNameSpace="form" translationKey="uploadPhotoButton" asFragment={true} />
					</div>
					<div className={replaceBtnClasses}>
						<svg>
							<use xlinkHref={`${breadcrumbs}#reload`} />
						</svg>
						<Text translationNameSpace="form" translationKey="replacePhotoButton" asFragment={true} />
					</div>
				</label>
				<div className={deleteBtnClasses} onClick={deletePhoto}>
					<svg>
						<use xlinkHref={`${breadcrumbs}#bin`} />
					</svg>
					<Text translationNameSpace="form" translationKey="deletePhotoButton" asFragment={true} />
				</div>
			</div>
		</div>
	);
};

export default FormDataImgUploader;
